import { NoticeConfig } from './types'

// 공지 HTML/JSON 이 제공되는 신뢰 도메인. iframe src·fetch 모두 이 도메인만 허용.
export const MAINTENANCE_HOST = 'maintenance.officemail.io'

// dev/staging 전용: notice.json mock 오버라이드(로컬 시나리오 테스트용).
const CONFIG_OVERRIDE_KEY = 'officemail.maintenance.configOverride'

/** dev mock notice.json 설정/해제. 운영 빌드에서는 fetch 가 무시한다. */
export function setMaintenanceConfigOverride(config: NoticeConfig | null): void {
  try {
    if (config) localStorage.setItem(CONFIG_OVERRIDE_KEY, JSON.stringify(config))
    else localStorage.removeItem(CONFIG_OVERRIDE_KEY)
  } catch {
    // 저장 불가 시 무시.
  }
}

/** 설정된 dev mock notice.json(없거나 운영 빌드면 null). 콘솔/e2e setMockConfig 용. */
export function getMaintenanceConfigOverride(): NoticeConfig | null {
  if (__ENV_PRODUCTION__) return null
  try {
    const raw = localStorage.getItem(CONFIG_OVERRIDE_KEY)
    if (raw) return JSON.parse(raw) as NoticeConfig
  } catch {
    // 파싱 실패 시 무시(실제 fetch 로 진행).
  }
  return null
}

/** url 이 신뢰 도메인인지 검증. iframe 주입/요청 전 반드시 통과시킨다. */
export function isAllowedMaintenanceUrl(url: string): boolean {
  try {
    return new URL(url).hostname === MAINTENANCE_HOST
  } catch {
    return false
  }
}

// 공지 HTML 이 클라이언트 JS 로 지원하는 언어(그 외는 ko 원문 유지).
const SUPPORTED_LANGS = ['ko', 'ja', 'en']

/** 앱 locale(예: ko-KR)을 공지 HTML 의 lang(ko|ja|en)으로 매핑. 미지원 시 undefined. */
export function toMaintenanceLang(locale?: string): string | undefined {
  if (!locale) return undefined
  const base = locale.toLowerCase().split('-')[0]
  return SUPPORTED_LANGS.includes(base) ? base : undefined
}

/**
 * 공지 HTML 에 언어를 강제 주입한다.
 *
 * HTML 의 다국어 JS 는 `?lang=` 쿼리 → `navigator.language` 순으로 언어를 고르는데,
 * srcDoc(about:srcdoc)에는 쿼리스트링이 없으므로 `navigator.language` 를 override 하는
 * 스크립트를 원본 JS 보다 먼저(head 직후) 주입해 앱 언어를 강제한다.
 * ko(또는 미지정)는 원문이므로 주입하지 않는다.
 */
export function injectMaintenanceLang(html: string, lang?: string): string {
  if (!lang || lang === 'ko') return html
  const inject = `<script>try{Object.defineProperty(navigator,'language',{configurable:true,get:function(){return ${JSON.stringify(
    lang,
  )}}})}catch(e){}</script>`
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head[^>]*>/i, (match) => match + inject)
  }
  return inject + html
}

/**
 * 공지/점검 HTML 본문을 문자열로 가져온다(iframe srcdoc 용).
 *
 * iframe `src` 로 직접 cross-origin url 을 로드하면 CDN 응답에 CORP 헤더가 없어
 * 부모 문서의 COEP 정책상 차단된다. 대신 fetch(CORS 허용)로 본문을 받아 srcdoc 에 넣으면
 * about:srcdoc(same-origin) 으로 렌더되어 CORP/COEP/X-Frame-Options 와 무관해진다.
 * HTML 은 자체완결형(인라인 SVG/CSS)이라 외부 리소스 의존이 없다.
 *
 * 신뢰 도메인이 아니거나 실패 시 null.
 */
export async function fetchMaintenanceHtml(url: string): Promise<string | null> {
  if (!isAllowedMaintenanceUrl(url)) return null
  try {
    const response = await fetch(url, { method: 'GET', cache: 'no-cache' })
    if (!response.ok) return null
    return await response.text()
  } catch {
    return null
  }
}

/**
 * notice.json 을 fetch 한다. 토큰 불필요한 공개 CDN 이므로 bare fetch 사용.
 *
 * 조건부 요청(If-None-Match)/304 는 브라우저 HTTP 캐시에 위임한다.
 * - JS 가 If-None-Match 를 수동으로 붙이면 non-simple request 가 되어 CORS preflight 가 발생하고,
 *   CDN preflight 가 해당 헤더를 허용하지 않으면 차단된다.
 * - cache:'no-cache' 는 브라우저가 자체적으로(safelisted) 재검증/304 처리를 하므로 simple request 로
 *   유지되어 preflight 없이 ETag 최적화 효과를 얻는다.
 *
 * 실패/네트워크 오류 시 null(공지 미표시, 정상 진입).
 */
export async function fetchNoticeConfig(url: string): Promise<NoticeConfig | null> {
  const override = getMaintenanceConfigOverride()
  if (override) return override

  if (!isAllowedMaintenanceUrl(url)) return null

  try {
    const response = await fetch(url, { method: 'GET', cache: 'no-cache' })
    if (!response.ok) return null
    return (await response.json()) as NoticeConfig
  } catch {
    return null
  }
}
